import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import PageTitle from '../components/PageTitle';
import { toast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';

const Auth = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData);
      toast({ 
        title: t('auth.loginSuccess') || 'Hoş geldiniz!', 
        description: t('auth.loginSuccessDesc') || 'Başarıyla giriş yapıldı.'
      });
      navigate('/presences');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Login error:', errorMsg);
      toast({ 
        title: t('auth.loginFailed') || 'Giriş Başarısız', 
        description: errorMsg.includes('credentials') || errorMsg.includes('password') || errorMsg.includes('email')
          ? t('auth.invalidCredentials') || 'E-posta veya şifre hatalı' 
          : errorMsg,
        variant: 'destructive'
      });
    }
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerData);
      toast({ 
        title: t('auth.registerSuccess') || 'Hesap Oluşturuldu!', 
        description: t('auth.registerSuccessDesc') || 'RepresenX\'e hoş geldiniz.'
      });
      navigate('/create');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Register error:', errorMsg);
      toast({ 
        title: t('auth.registerFailed') || 'Kayıt Başarısız', 
        description: errorMsg.includes('exists') || errorMsg.includes('already')
          ? t('auth.emailExists') || 'Bu e-posta adresi zaten kullanılıyor'
          : errorMsg,
        variant: 'destructive'
      });
    }
    setIsLoading(false);
  };

  return (
    <div className="bg-black text-white min-h-screen flex items-center justify-center">
      <PageTitle title="Login & Register - Get Started" />
      <div className="max-w-md w-full px-6">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_layout-kit-1/artifacts/fbm0hm7y_Represenx_logo_trans.png" 
            alt="RepresenX" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold">{t('landing.welcome')}</h1>
          <p className="text-gray-400 mt-2">{t('landing.subtitle')}</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-900">
            <TabsTrigger value="login" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              {t('auth.login')}
            </TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              {t('auth.register')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30">
              <CardHeader>
                <CardTitle>{t('auth.login')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="bg-gray-800 border-cyan-500/30"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder={t('auth.passwordPlaceholder')}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="bg-gray-800 border-cyan-500/30"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
                    disabled={isLoading}
                  >
                    {isLoading ? `${t('common.loading')}` : t('auth.loginButton')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30">
              <CardHeader>
                <CardTitle>{t('auth.createAccount')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-name">{t('auth.name')}</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder={t('auth.namePlaceholder')}
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      className="bg-gray-800 border-cyan-500/30"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-email">{t('auth.email')}</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="bg-gray-800 border-cyan-500/30"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-password">{t('auth.password')}</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder={t('auth.passwordPlaceholder')}
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="bg-gray-800 border-cyan-500/30"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
                    disabled={isLoading}
                  >
                    {isLoading ? `${t('common.loading')}` : t('auth.registerButton')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Auth;
